import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { WebshopEditorProvider, useWebshopEditor } from "@/components/WebshopEditor";
import { useTranslation } from "react-i18next";
import {
  ShoppingBag,
  ShoppingCart,
  Search,
  Filter,
  Package,
  Star,
  ArrowLeft,
  Plus,
  Minus,
  Shirt,
  Wrench,
  HardHat,
  Box,
  ChevronRight,
  Sparkles,
  Clock,
  Flame,
  Paintbrush
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for dynamic categories
const CATEGORY_ICONS = {
  all: <Package className="w-4 h-4" />,
  clothing: <Shirt className="w-4 h-4" />,
  tools: <Wrench className="w-4 h-4" />,
  safety_gear: <HardHat className="w-4 h-4" />,
  bags: <ShoppingBag className="w-4 h-4" />,
  accessories: <Box className="w-4 h-4" />,
  scaffolding: <Package className="w-4 h-4" />,
  nets_tarps: <Box className="w-4 h-4" />,
  default: <Package className="w-4 h-4" />
};

// Card style definitions
const CARD_STYLES = {
  classic: {
    borderRadius: "12px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-square",
    buttonStyle: "rounded-lg",
    priceSize: "text-xl",
    hoverEffect: "hover:border-teal-500/50"
  },
  modern: {
    borderRadius: "16px",
    shadow: "0 4px 20px rgba(0,0,0,0.3)",
    borderWidth: "0px",
    imageRatio: "aspect-[4/3]",
    buttonStyle: "rounded-xl",
    priceSize: "text-2xl",
    hoverEffect: "hover:-translate-y-2 hover:shadow-2xl"
  },
  minimal: {
    borderRadius: "8px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-video",
    buttonStyle: "rounded-md",
    priceSize: "text-lg",
    hoverEffect: "hover:opacity-90"
  },
  bold: {
    borderRadius: "20px",
    shadow: "0 8px 30px rgba(0,0,0,0.4)",
    borderWidth: "2px",
    imageRatio: "aspect-square",
    buttonStyle: "rounded-full",
    priceSize: "text-3xl",
    hoverEffect: "hover:scale-105"
  },
  compact: {
    borderRadius: "8px",
    shadow: "none",
    borderWidth: "1px",
    imageRatio: "aspect-[3/2]",
    buttonStyle: "rounded-md",
    priceSize: "text-base",
    hoverEffect: "hover:border-teal-500/50"
  },
  elegant: {
    borderRadius: "4px",
    shadow: "0 2px 15px rgba(0,0,0,0.2)",
    borderWidth: "1px",
    imageRatio: "aspect-[3/4]",
    buttonStyle: "rounded-none",
    priceSize: "text-xl",
    hoverEffect: "hover:shadow-lg"
  }
};

// Countdown Timer Component for Sale Products
function SaleCountdown({ endDate }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    if (!endDate) return;
    
    // Parse the end date - add time to end of day if only date provided
    const saleEndDate = new Date(endDate);
    if (endDate.length === 10) { // Format: YYYY-MM-DD
      saleEndDate.setHours(23, 59, 59, 999);
    }
    
    const timer = setInterval(() => {
      const now = new Date();
      const diff = saleEndDate - now;
      
      if (diff <= 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [endDate]);
  
  if (!endDate) return null;
  
  return (
    <div className="flex items-center gap-1 text-xs">
      <Clock className="w-3 h-3" />
      <span>{timeLeft.days}d</span>
      <span>{String(timeLeft.hours).padStart(2, '0')}h</span>
      <span>{String(timeLeft.minutes).padStart(2, '0')}m</span>
      <span>{String(timeLeft.seconds).padStart(2, '0')}s</span>
    </div>
  );
}

export default function PublicWebshop() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [appearance, setAppearance] = useState(null);
  
  // Check if user is admin (can edit webshop)
  const isAdmin = user && ['platform_admin', 'webshop_admin'].includes(user.role);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAppearance();
    loadCart();
  }, []);

  const fetchAppearance = async () => {
    try {
      const res = await axios.get(`${API}/webshop-appearance`);
      setAppearance(res.data);
      // Apply CSS variables to document
      const root = document.documentElement;
      Object.entries(res.data).forEach(([key, value]) => {
        if (typeof value === 'string' && value.startsWith('#')) {
          root.style.setProperty(`--ws-${key.replace(/_/g, '-')}`, value);
        }
      });
    } catch (err) {
      console.error("Error fetching appearance:", err);
    }
  };

  // Category translation mapping
  const categoryTranslations = {
    'scaffolding': t('webshop.scaffoldingEquipment'),
    'Scaffolding Equipment': t('webshop.scaffoldingEquipment'),
    'safety_gear': t('webshop.safetyGear'),
    'Safety Gear': t('webshop.safetyGear'),
    'clothing': t('webshop.workClothing'),
    'Work Clothing': t('webshop.workClothing'),
    'tools': t('webshop.tools'),
    'Tools': t('webshop.tools'),
    'accessories': t('webshop.accessories'),
    'Accessories': t('webshop.accessories'),
    'nets_tarps': t('webshop.netsTarps'),
    'Nets & Tarps': t('webshop.netsTarps'),
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/shop/categories`);
      // Add "All" category at the start and map icons + translations
      const apiCategories = res.data.map(cat => ({
        ...cat,
        name: categoryTranslations[cat.name] || categoryTranslations[cat.id] || cat.name,
        icon: CATEGORY_ICONS[cat.id] || CATEGORY_ICONS.default
      }));
      setCategories([
        { id: "all", name: t('webshop.all'), icon: CATEGORY_ICONS.all },
        ...apiCategories
      ]);
    } catch (err) {
      console.error("Error fetching categories:", err);
      // Fallback to default categories
      setCategories([
        { id: "all", name: t('webshop.all'), icon: CATEGORY_ICONS.all },
        { id: "scaffolding", name: t('webshop.scaffoldingEquipment'), icon: CATEGORY_ICONS.scaffolding },
        { id: "safety_gear", name: t('webshop.safetyGear'), icon: CATEGORY_ICONS.safety_gear },
        { id: "clothing", name: t('webshop.workClothing'), icon: CATEGORY_ICONS.clothing },
        { id: "tools", name: t('webshop.tools'), icon: CATEGORY_ICONS.tools },
        { id: "accessories", name: t('webshop.accessories'), icon: CATEGORY_ICONS.accessories },
      ]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/shop/products/public`);
      setProducts(res.data);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem("allstillas_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const saveCart = (newCart) => {
    localStorage.setItem("allstillas_cart", JSON.stringify(newCart));
    setCart(newCart);
  };

  const addToCart = (product, size = null) => {
    const cartItem = {
      product_id: product.product_id,
      name: product.name,
      price: product.price,
      photo: product.photos?.[0] || null,
      size: size,
      quantity: 1
    };

    const existingIndex = cart.findIndex(
      item => item.product_id === product.product_id && item.size === size
    );

    let newCart;
    if (existingIndex >= 0) {
      newCart = [...cart];
      newCart[existingIndex].quantity += 1;
    } else {
      newCart = [...cart, cartItem];
    }

    saveCart(newCart);
    toast.success(`${product.name} added to cart!`);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  
  // Check if there are discounted products
  const discountedProducts = products.filter(p => p.discount_enabled && p.discount_percent > 0);
  const hasDiscountedProducts = discountedProducts.length > 0;
  const maxDiscount = hasDiscountedProducts ? Math.max(...discountedProducts.map(p => p.discount_percent || 0)) : 0;
  
  // Find the soonest ending discount date for the banner (excluding permanent discounts)
  const soonestDiscountEndDate = discountedProducts
    .filter(p => p.discount_end_date && p.discount_permanent !== true)
    .map(p => ({ date: new Date(p.discount_end_date), original: p.discount_end_date }))
    .filter(d => d.date > new Date())
    .sort((a, b) => a.date - b.date)[0]?.original || null;
  
  // State for filtering discounted items
  const [showOnlyDiscounted, setShowOnlyDiscounted] = useState(false);

  const filteredProducts = products
    .filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === "all" || p.category === selectedCategory;
      const matchesDiscount = !showOnlyDiscounted || (p.discount_enabled && p.discount_percent > 0);
      return matchesSearch && matchesCategory && matchesDiscount;
    })
    // Sort discounted products to top
    .sort((a, b) => {
      const aDiscount = a.discount_enabled && a.discount_percent > 0 ? a.discount_percent : 0;
      const bDiscount = b.discount_enabled && b.discount_percent > 0 ? b.discount_percent : 0;
      return bDiscount - aDiscount; // Higher discount first
    });

  return (
    <WebshopEditorProvider isAdmin={isAdmin}>
      <div className="min-h-screen" style={{ backgroundColor: appearance?.page_bg || '#050505' }}>
        {/* Header */}
        <header 
          className="sticky top-0 z-50 backdrop-blur border-b border-white/10 pt-[env(safe-area-inset-top,0px)]"
          style={{ backgroundColor: `${appearance?.header_bg || '#050505'}F2` }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{t('common.back')}</span>
              </Link>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg"
                  style={{ 
                    background: `linear-gradient(to bottom right, ${appearance?.hero_gradient_from || '#FF6B00'}, ${appearance?.hero_gradient_via || '#F7931A'}, ${appearance?.hero_gradient_to || '#FFCD00'})`,
                    boxShadow: `0 10px 15px -3px ${appearance?.hero_gradient_via || '#F7931A'}66`
                  }}
                >
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 
                    className="text-xl font-['Unbounded'] font-bold"
                    style={{ color: appearance?.header_text || '#FFFFFF' }}
                  >
                    {appearance?.shop_name || 'AllStillas Shop'}
                  </h1>
                  <p 
                    className="text-xs"
                    style={{ color: appearance?.header_accent || '#F7931A' }}
                  >
                    {appearance?.shop_tagline || 'Professional Equipment'}
                  </p>
                </div>
              </div>
            </div>

            <Link 
              to="/cart" 
              className="relative group"
              data-testid="cart-button"
            >
              <Button 
                className="text-white gap-2 font-bold"
                style={{ 
                  background: `linear-gradient(to right, ${appearance?.btn_primary_from || '#FF6B00'}, ${appearance?.btn_primary_to || '#F7931A'})`,
                  color: appearance?.btn_primary_text || '#FFFFFF',
                  boxShadow: `0 10px 15px -3px ${appearance?.btn_primary_to || '#F7931A'}66`
                }}
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="hidden sm:inline">{t('webshop.cart')}</span>
                {cartCount > 0 && (
                  <Badge 
                    className="ml-1 font-bold"
                    style={{ backgroundColor: '#FFFFFF', color: appearance?.btn_primary_to || '#F7931A' }}
                  >
                    {cartCount}
                  </Badge>
                )}
              </Button>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
            </Link>
            
            {/* Edit Design Button for Admins */}
            {isAdmin && (
              <Link to="/admin/webshop-appearance">
                <Button 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 gap-2 font-bold shadow-lg"
                  data-testid="edit-design-btn"
                >
                  <Paintbrush className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit Design</span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Edit Design Floating Button for Admins */}
      {isAdmin && (
        <div className="fixed bottom-6 right-6 z-50">
          <Link to="/admin/webshop-appearance">
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 gap-2 font-bold shadow-2xl shadow-purple-500/50 px-6 py-6 rounded-full"
              data-testid="floating-edit-btn"
            >
              <Paintbrush className="w-5 h-5" />
              <span>Edit Design</span>
            </Button>
          </Link>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* SALE Banner with Countdown - Only shows when there are discounted products */}
        {hasDiscountedProducts && (
          <div 
            onClick={() => setShowOnlyDiscounted(!showOnlyDiscounted)}
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FF0000] via-[#FF6B00] to-[#FFCD00] p-4 mb-6 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#FF6B00]/50 ${showOnlyDiscounted ? 'ring-4 ring-white/50' : 'animate-pulse'}`}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-white text-2xl">
                  <Flame className="w-8 h-8 animate-bounce" />
                  <span className="font-black">🔥 {t('webshop.sale')}</span>
                  <Flame className="w-8 h-8 animate-bounce" />
                </div>
                <span className="text-white font-bold text-lg">{t('webshop.upToOff', { percent: maxDiscount })}</span>
                <Badge className="bg-white text-red-600 font-bold ml-2">
                  {discountedProducts.length} {discountedProducts.length > 1 ? t('webshop.items') : t('webshop.item')}
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                {soonestDiscountEndDate && (
                  <div className="bg-black/30 rounded-full px-4 py-2 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white" />
                    <span className="text-white font-medium">{t('webshop.endsIn')}:</span>
                    <SaleCountdown endDate={soonestDiscountEndDate} />
                  </div>
                )}
                <Button 
                  className={`${showOnlyDiscounted ? 'bg-white text-red-600' : 'bg-black/40 text-white'} font-bold hover:bg-white hover:text-red-600`}
                  onClick={(e) => { e.stopPropagation(); setShowOnlyDiscounted(!showOnlyDiscounted); }}
                >
                  {showOnlyDiscounted ? `✓ ${t('webshop.showingDeals')}` : t('webshop.viewDeals')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Hero Banner */}
        <div 
          className="relative overflow-hidden rounded-2xl p-8 mb-8"
          style={{ 
            background: `linear-gradient(to right, ${appearance?.hero_gradient_from || '#FF6B00'}33, ${appearance?.hero_gradient_via || '#F7931A'}33, ${appearance?.hero_gradient_to || '#FFCD00'}33)`,
            borderColor: `${appearance?.hero_gradient_via || '#F7931A'}66`,
            borderWidth: '1px'
          }}
        >
          <div className="absolute inset-0 opacity-10"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#F7931A]" />
              <span className="text-[#F7931A] text-sm font-medium">AllStillas {t('webshop.officialShop')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-2">
              {t('webshop.professionalEquipment')}
            </h2>
            <p className="text-[#94A3B8] max-w-xl">
              {t('webshop.findEverything')}
            </p>
          </div>
          <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[#F7931A]/30 rounded-full blur-3xl"></div>
          <div className="absolute -left-10 -top-10 w-32 h-32 bg-[#FF6B00]/20 rounded-full blur-3xl"></div>
        </div>

        {/* Used Scaffolding Banner - Link to bruktstillas.com */}
        <a 
          href="https://bruktstillas.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block mb-8"
        >
          <div 
            className="relative overflow-hidden rounded-2xl p-6 border border-[#10b981]/30 bg-gradient-to-r from-[#10b981]/20 via-[#059669]/20 to-[#047857]/20 hover:border-[#10b981]/60 transition-all group cursor-pointer hover:scale-[1.01]"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#10b981]/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Package className="w-7 h-7 text-[#10b981]" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-[#10b981]/20 text-[#10b981] border-[#10b981]/30">
                      ♻️ Brukt Stillas
                    </Badge>
                    <Badge className="bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30">
                      Gode Priser
                    </Badge>
                  </div>
                  <h3 className="text-xl font-['Unbounded'] font-bold text-white">
                    Ser du etter brukt stillas?
                  </h3>
                  <p className="text-[#94A3B8] text-sm">
                    Besøk vår spesialbutikk for kvalitetssikret brukt stillasutstyr
                  </p>
                </div>
              </div>
              <Button 
                className="bg-[#10b981] text-white hover:bg-[#059669] font-bold gap-2 px-6 group-hover:gap-3 transition-all"
              >
                bruktstillas.com
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-[#10b981]/20 rounded-full blur-3xl"></div>
          </div>
        </a>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('webshop.searchProducts')}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] h-12"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.id)}
                className={`whitespace-nowrap gap-2 ${
                  selectedCategory === cat.id
                    ? "bg-teal-500 text-white hover:bg-teal-600"
                    : "border-white/20 text-[#94A3B8] hover:border-teal-500/50 hover:text-white"
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.icon}
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-[#475569] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('webshop.noProductsFound')}</h3>
            <p className="text-[#94A3B8]">{t('webshop.tryDifferentSearch')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch">
            {filteredProducts.map((product) => {
              const cardStyle = CARD_STYLES[appearance?.card_style] || CARD_STYLES.classic;
              return (
              <Card 
                key={product.product_id} 
                className={`overflow-hidden group transition-all duration-300 cursor-pointer flex flex-col h-full ${cardStyle.hoverEffect}`}
                style={{
                  backgroundColor: appearance?.card_bg || '#0A0A0A',
                  borderColor: appearance?.card_border || '#1F1F1F',
                  borderWidth: cardStyle.borderWidth,
                  borderRadius: cardStyle.borderRadius,
                  boxShadow: cardStyle.shadow
                }}
                data-testid={`product-card-${product.product_id}`}
              >
                {/* Clickable area for product detail */}
                <Link to={`/webshop/product/${product.product_id}`}>
                  <div className={`${cardStyle.imageRatio} relative overflow-hidden`} style={{ backgroundColor: '#FFFFFF' }}>
                    {product.photos && product.photos[0] ? (
                      (() => {
                        const imgSettings = product.image_settings?.[product.photos[0]] || { zoom: 1, position_x: 50, position_y: 50 };
                        return (
                          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: '#FFFFFF' }}>
                            <img
                              src={product.photos[0]}
                              alt={product.name}
                              className="absolute group-hover:scale-105 transition-transform duration-300"
                              style={{
                                width: `${imgSettings.zoom * 100}%`,
                                height: `${imgSettings.zoom * 100}%`,
                                left: `${imgSettings.position_x - (imgSettings.zoom * 50)}%`,
                                top: `${imgSettings.position_y - (imgSettings.zoom * 50)}%`,
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        );
                      })()
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-black/50">
                        <Package className="w-20 h-20 text-[#475569]" />
                      </div>
                    )}
                    {/* Discount badge with mini countdown */}
                    {product.discount_enabled && product.discount_percent > 0 && (
                      <div className="absolute top-3 left-3 flex flex-col gap-1">
                        <Badge 
                          className="font-bold text-lg px-3 py-1 animate-pulse shadow-lg"
                          style={{ 
                            backgroundColor: appearance?.badge_sale_bg || '#EF4444',
                            color: appearance?.badge_sale_text || '#FFFFFF'
                          }}
                        >
                          🔥 -{product.discount_percent}% 🔥
                        </Badge>
                        {product.discount_end_date && !product.discount_permanent && (
                          <div className="bg-black/80 text-white text-[10px] px-2 py-1 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3 text-[#FFCD00]" />
                            <SaleCountdown endDate={product.discount_end_date} />
                          </div>
                        )}
                      </div>
                    )}
                    {product.stock_quantity < 5 && product.stock_quantity > 0 && (
                      <Badge className="absolute top-3 right-3 bg-orange-500">Low Stock</Badge>
                    )}
                    {product.stock_quantity === 0 && (
                      <Badge className="absolute top-3 right-3 bg-red-500">Out of Stock</Badge>
                    )}
                    {/* View details overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white font-medium bg-[#F7931A] px-4 py-2 rounded-full">
                        View details
                      </span>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-4 flex flex-col flex-1">
                  {/* Seller Info */}
                  {product.seller_code && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                      {product.seller_logo ? (
                        <img 
                          src={product.seller_logo} 
                          alt={product.seller_name}
                          className="w-6 h-6 rounded object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded bg-[#F7931A]/20 flex items-center justify-center">
                          <span className="text-[#F7931A] text-xs font-bold">
                            {product.seller_name?.charAt(0) || 'S'}
                          </span>
                        </div>
                      )}
                      <span className="text-xs text-[#94A3B8] truncate flex-1">
                        {product.seller_name || 'AllStillas'}
                      </span>
                      {product.seller_verified && (
                        <span className="text-blue-400 text-xs" title="Verifisert selger">✓</span>
                      )}
                    </div>
                  )}
                  
                  {/* Product info - fixed height section */}
                  <div className="flex-1 space-y-2">
                    <Link to={`/webshop/product/${product.product_id}`}>
                      <div>
                        <p 
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: appearance?.badge_category_bg || '#F7931A' }}
                        >
                          {categories.find(c => c.id === product.category)?.name || product.category}
                        </p>
                        <h3 
                          className="text-lg font-semibold transition-colors line-clamp-2 min-h-[3.5rem]"
                          style={{ color: appearance?.card_text || '#FFFFFF' }}
                        >
                          {product.name}
                        </h3>
                        {product.description && (
                          <p 
                            className="text-sm line-clamp-2 mt-1 min-h-[2.5rem]"
                            style={{ color: appearance?.card_text_secondary || '#94A3B8' }}
                          >
                            {product.description}
                          </p>
                        )}
                        {!product.description && (
                          <div className="min-h-[2.5rem]"></div>
                        )}
                      </div>
                    </Link>
                    
                    {/* Price with discount support */}
                    <div className="space-y-1">
                      {product.discount_enabled && product.discount_percent > 0 ? (
                        <div className="flex items-baseline gap-2">
                          <p 
                            className={`${cardStyle.priceSize} font-bold`}
                            style={{ color: appearance?.price_color || '#23F7DD' }}
                          >
                            {(product.price * (1 - product.discount_percent / 100)).toFixed(0)} NOK
                          </p>
                          <p 
                            className="text-lg line-through"
                            style={{ color: appearance?.price_old_color || '#6B7280' }}
                          >
                            {product.price} NOK
                          </p>
                        </div>
                      ) : (
                        <p 
                          className={`${cardStyle.priceSize} font-bold`}
                          style={{ color: appearance?.price_color || '#23F7DD' }}
                        >
                          {product.price} NOK
                        </p>
                      )}
                      {/* Price ex VAT for businesses */}
                      <p className="text-xs text-[#64748B]">
                        Ex. MVA: {(product.price_ex_vat || product.price / 1.25).toFixed(0)} NOK
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="border-white/20 text-[#94A3B8]">
                        Stock: {product.stock_quantity}
                      </Badge>
                    </div>

                    {/* Size selector for clothing */}
                    {product.category === "clothing" && product.sizes && product.sizes.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {product.sizes.map(size => (
                          <button
                            key={size}
                            onClick={(e) => { e.stopPropagation(); setSelectedSizes(prev => ({ ...prev, [product.product_id]: size })); }}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              selectedSizes[product.product_id] === size
                                ? "bg-[#F7931A] text-white"
                                : "bg-white/10 text-[#94A3B8] hover:bg-white/20"
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Button always at bottom - FIRE for discounted products, customizable for normal! */}
                  <Button
                    onClick={(e) => { e.stopPropagation(); addToCart(product, selectedSizes[product.product_id] || null); }}
                    disabled={product.stock_quantity === 0}
                    className={`w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed font-bold ${cardStyle.buttonStyle}`}
                    style={
                      product.discount_enabled && product.discount_percent > 0
                        ? {
                            background: `linear-gradient(to right, ${appearance?.badge_sale_bg || '#EF4444'}, ${appearance?.btn_primary_from || '#FF6B00'}, ${appearance?.btn_primary_to || '#F7931A'})`,
                            color: appearance?.btn_primary_text || '#FFFFFF'
                          }
                        : {
                            background: `linear-gradient(to right, ${appearance?.btn_primary_from || '#FF6B00'}, ${appearance?.btn_primary_to || '#F7931A'})`,
                            color: appearance?.btn_primary_text || '#FFFFFF'
                          }
                    }
                    data-testid={`add-to-cart-${product.product_id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {product.discount_enabled && product.discount_percent > 0 ? (
                      <span className="flex items-center gap-1">🔥 Add to Cart 🔥</span>
                    ) : (
                      "Add to Cart"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
            })}
          </div>
        )}

        {/* Cart Summary (floating) */}
        {cartCount > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
            <Link to="/cart">
              <Button 
                className="bg-teal-500 text-white px-8 py-6 text-lg rounded-full shadow-2xl shadow-teal-500/50 hover:bg-teal-600 gap-3 font-bold"
                data-testid="floating-cart-button"
              >
                <ShoppingCart className="w-6 h-6" />
                <span>View Cart ({cartCount})</span>
                <ChevronRight className="w-5 h-5" />
                <span className="font-black">{cartTotal.toLocaleString()} NOK</span>
              </Button>
            </Link>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
              <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
            </div>
            <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </Link>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </WebshopEditorProvider>
  );
}
